/**
 * **Der Feiertagshinweis — nur Anzeige** (08.10.2026, Migration 0287).
 *
 * Luca: *„Feiertage zählen weiter als Miettag. … bereite das vor, dass bei
 * der Angebotsstellung oder bei Schnellvermietungen, wenn es einen Feiertag
 * dazwischen gibt, das angezeigt wird."*
 *
 * Gerechnet wird in `@mrmachine/fach` (`feiertage.ts`) — hier in der
 * Oberfläche, nicht über eine Route: Die Liste ist eine reine Funktion aus
 * Jahr und Land, und das Land kommt einmal mit `GET /ich`. Eine Abfrage je
 * Zeile oder je Zeitraum wäre Last ohne Gewinn.
 *
 * **Kein Betrag hängt daran.** Die Miettage rechnet weiter die andere Seite,
 * und ein Feiertag ist darin ein Miettag (Rechenreferenz §3.1).
 */

import { feiertageImZeitraum } from "@mrmachine/fach";
import type { Bundesland, Feiertag } from "@mrmachine/fach";
import { tagImBetrieb, tagKurz, wochentagVon } from "./betriebstag";

type Uebersetze = ((pfad: string, werte?: Record<string, string | number>) => string)
  & { liste: (pfad: string) => string[] };

/** Die Feiertage zwischen zwei Tagen oder Zeitpunkten, beide eingeschlossen (deutscher Kalendertag). */
export function feiertageZwischen(
  von: string | null | undefined,
  bis: string | null | undefined,
  land: Bundesland,
): Feiertag[] {
  const a = tagImBetrieb(von ?? "");
  const b = tagImBetrieb(bis ?? von ?? "");
  if (!a || !b) return [];
  return feiertageImZeitraum(a, b, land);
}

/** Der Name eines Feiertags — „Reformationstag". */
export function feiertagName(t: Uebersetze, f: Feiertag): string {
  return t(`feiertage.name.${f.schluessel}`);
}

/** Ein Feiertag mit Wochentag und Datum — „Sa 31.10. Reformationstag". */
export function feiertagEintrag(t: Uebersetze, f: Feiertag): string {
  return t("feiertage.eintrag", {
    wt: t.liste("kalender.wochentageKurz")[wochentagVon(f.tag)] ?? "",
    datum: tagKurz(f.tag),
    name: feiertagName(t, f),
  });
}

/**
 * Der leise Satz unter einem Zeitraum: ein Feiertag mit Namen, mehrere als
 * Zahl — die Namen stehen dann im `title`. `null`, wenn keiner darin liegt.
 * Mit `miettag` hängt „zählt als Miettag" daran (Merkliste, Abschluss).
 */
export function feiertagssatz(
  t: Uebersetze,
  liste: Feiertag[],
  miettag = false,
): { text: string; titel: string } | null {
  const [erster] = liste;
  if (!erster) return null;
  const titel = liste.map((f) => feiertagEintrag(t, f)).join("\n");
  const kern = liste.length === 1
    ? t("feiertage.enthaelt", { eintrag: feiertagEintrag(t, erster) })
    : t("feiertage.enthaeltMehrere", { zahl: liste.length });
  return {
    text: miettag ? t("feiertage.mitMiettag", { satz: kern }) : kern,
    titel: miettag ? `${titel}\n${t("feiertage.zaehltAlsMiettag")}` : titel,
  };
}

/** Mehrere Zeiträume, ein Satz: jeder Feiertag einmal, nach Datum (Angebot). */
export function feiertageUeber(
  zeitraeume: { von?: string | null; bis?: string | null }[],
  land: Bundesland,
): Feiertag[] {
  const je = new Map<string, Feiertag>();
  for (const z of zeitraeume) {
    for (const f of feiertageZwischen(z.von, z.bis, land)) je.set(f.tag, f);
  }
  return [...je.values()].sort((a, b) => a.tag.localeCompare(b.tag));
}
